import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon.js';

export type ToastTone = 'info' | 'success' | 'error';

interface ToastEntry {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: ReactNode;
}

interface ToastApi {
  show: (tone: ToastTone, message: ReactNode, durationMs?: number) => void;
  info: (message: ReactNode, durationMs?: number) => void;
  success: (message: ReactNode, durationMs?: number) => void;
  error: (message: ReactNode, durationMs?: number) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

const ICONS: Record<ToastTone, React.FC<{ size?: number }>> = {
  info: Icon.Info,
  success: Icon.CheckCircle,
  error: Icon.Warn,
};

export function useToast(): ToastApi {
  const api = useContext(ToastCtx);
  if (!api) throw new Error('useToast must be used inside <ToastHost>');
  return api;
}

export function ToastHost({ children }: { readonly children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  const show = useCallback((tone: ToastTone, message: ReactNode, durationMs = 4000) => {
    const id = ++idRef.current;
    setItems((prev) => [...prev, { id, tone, message }]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, durationMs);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      info: (msg, ms) => show('info', msg, ms),
      success: (msg, ms) => show('success', msg, ms),
      error: (msg, ms) => show('error', msg, ms),
    }),
    [show],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toast-wrap" aria-live="polite" aria-atomic="false">
        {items.map((t) => {
          const ToneIcon = ICONS[t.tone];
          return (
            <div key={t.id} className={`toast ${t.tone}`} role={t.tone === 'error' ? 'alert' : 'status'}>
              <ToneIcon size={16} />
              <span>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}
